import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Create a base axios instance for collection operations
const adminCollectionApi = axios.create({
  baseURL: `${API_URL}/api/v1/admin`,
});

// Request interceptor to add token
adminCollectionApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
adminCollectionApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const adminCollectionService = {
  // ==================== COLLECTIONS ====================
  /**
   * Get all collections with filters and pagination
   */
  getAllCollections: async (params = {}) => {
    console.log('📤 [CollectionService] Fetching all collections...', params);
    try {
      const response = await adminCollectionApi.get('/collections', { params });
      console.log('📥 [CollectionService] Collections fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error fetching collections:', error);
      throw error;
    }
  },

  /**
   * Get collections for a specific product
   */
  getCollectionsByProduct: async (productId, params = {}) => {
    console.log('📤 [CollectionService] Fetching collections for product:', productId, params);
    try {
      const response = await adminCollectionApi.get(`/products/${productId}/collections`, { params });
      console.log('📥 [CollectionService] Collections fetched for product:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error fetching collections for product:', error);
      throw error;
    }
  },

  /**
   * Get collection by ID
   */
  getCollectionById: async (collectionId) => {
    console.log('📤 [CollectionService] Fetching collection:', collectionId);
    try {
      const response = await adminCollectionApi.get(`/collections/${collectionId}`);
      console.log('📥 [CollectionService] Collection fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error fetching collection:', error);
      throw error;
    }
  },

  /**
   * Create new collection (handles all types - parent, sub, standalone)
   */
  createCollection: async (collectionData) => {
    console.log('📤 [CollectionService] Creating collection:', collectionData);
    try {
      const response = await adminCollectionApi.post('/collections', collectionData);
      console.log('📥 [CollectionService] Collection created:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error creating collection:', error);
      throw error;
    }
  },

  /**
   * Update collection
   */
  updateCollection: async (collectionId, collectionData) => {
    console.log('📤 [CollectionService] Updating collection:', collectionId);
    try {
      const response = await adminCollectionApi.put(`/collections/${collectionId}`, collectionData);
      console.log('📥 [CollectionService] Collection updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error updating collection:', error);
      throw error;
    }
  },

  /**
   * Update collection status
   */
  updateCollectionStatus: async (collectionId, status) => {
    console.log('📤 [CollectionService] Updating collection status:', collectionId, status);
    try {
      const response = await adminCollectionApi.patch(`/collections/${collectionId}/status`, { status });
      console.log('📥 [CollectionService] Collection status updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error updating collection status:', error);
      throw error;
    }
  },

  /**
   * Delete collection (soft delete - archive)
   */
  deleteCollection: async (collectionId) => {
    console.log('📤 [CollectionService] Deleting collection:', collectionId);
    try {
      const response = await adminCollectionApi.delete(`/collections/${collectionId}`);
      console.log('📥 [CollectionService] Collection deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error deleting collection:', error);
      throw error;
    }
  },

  // ==================== PARENT COLLECTIONS ====================
  /**
   * Get parent collections for a specific product (for sub collections)
   */
  getParentCollectionsByProduct: async (productId) => {
    console.log('📤 [CollectionService] Fetching parent collections for product:', productId);
    try {
      const response = await adminCollectionApi.get(`/products/${productId}/collections/parents`);
      console.log('📥 [CollectionService] Parent collections fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error fetching parent collections:', error);
      throw error;
    }
  },

  /**
   * Get all parent collections (for standalone placement)
   */
  getAllParentCollections: async (params = {}) => {
    console.log('📤 [CollectionService] Fetching all parent collections...', params);
    try {
      const response = await adminCollectionApi.get('/collections/parents/all', { params });
      console.log('📥 [CollectionService] All parent collections fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error fetching all parent collections:', error);
      throw error;
    }
  },

  // ==================== SUB COLLECTIONS ====================
  /**
   * Get sub collections for a parent collection
   */
  getSubCollectionsByParent: async (parentId, params = {}) => {
    console.log('📤 [CollectionService] Fetching sub collections for parent:', parentId, params);
    try {
      const response = await adminCollectionApi.get(`/collections/${parentId}/sub-collections`, { params });
      console.log('📥 [CollectionService] Sub collections fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error fetching sub collections:', error);
      throw error;
    }
  },

  // ==================== STANDALONE COLLECTIONS ====================
  /**
   * Get standalone collections
   */
  getStandaloneCollections: async (params = {}) => {
    console.log('📤 [CollectionService] Fetching standalone collections...', params);
    try {
      const response = await adminCollectionApi.get('/collections/standalone', { params });
      console.log('📥 [CollectionService] Standalone collections fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error fetching standalone collections:', error);
      throw error;
    }
  },

  /**
   * Create standalone collection (legacy)
   */
  createStandaloneCollection: async (collectionData) => {
    console.log('📤 [CollectionService] Creating standalone collection:', collectionData);
    try {
      const response = await adminCollectionApi.post('/collections/standalone', collectionData);
      console.log('📥 [CollectionService] Standalone collection created:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error creating standalone collection:', error);
      throw error;
    }
  },

  // ==================== COLLECTION STATISTICS ====================
  /**
   * Get collection statistics
   */
  getCollectionStats: async () => {
    console.log('📤 [CollectionService] Fetching collection statistics...');
    try {
      const response = await adminCollectionApi.get('/collections/stats');
      console.log('📥 [CollectionService] Collection statistics fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error fetching collection statistics:', error);
      throw error;
    }
  },

  // ==================== COLLECTION SEARCH ====================
  /**
   * Search collections
   */
  searchCollections: async (query, params = {}) => {
    console.log('📤 [CollectionService] Searching collections:', query);
    try {
      const response = await adminCollectionApi.get('/collections/search', {
        params: { ...params, query }
      });
      console.log('📥 [CollectionService] Collections search results:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error searching collections:', error);
      throw error;
    }
  },

  // ==================== COLLECTION PRODUCTS ====================
  /**
   * Get products in a specific collection
   */
  getCollectionProducts: async (collectionId, params = {}) => {
    console.log('📤 [CollectionService] Fetching products for collection:', collectionId);
    try {
      const response = await adminCollectionApi.get(`/collections/${collectionId}/products`, { params });
      console.log('📥 [CollectionService] Collection products fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error fetching collection products:', error);
      throw error;
    }
  },



  getCollectionBasedProducts: async (params = {}) => {
    const response = await adminCollectionApi.get('/collection-based', { params });
    return response.data;
  },

  // ==================== BULK OPERATIONS ====================
  /**
   * Bulk update collections
   */
  bulkUpdateCollections: async (collectionIds, data) => {
    console.log('📤 [CollectionService] Bulk updating collections:', collectionIds);
    try {
      const response = await adminCollectionApi.post('/collections/bulk-update', {
        collectionIds,
        data
      });
      console.log('📥 [CollectionService] Collections bulk updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error bulk updating collections:', error);
      throw error;
    }
  },

  // ==================== EXPORT ====================
  /**
   * Export collections
   */
  exportCollections: async (format = 'csv') => {
    console.log('📤 [CollectionService] Exporting collections...');
    try {
      const response = await adminCollectionApi.get('/collections/export', {
        params: { format },
        responseType: 'blob'
      });
      console.log('📥 [CollectionService] Collections exported');
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionService] Error exporting collections:', error);
      throw error;
    }
  },

  // ==================== UTILITIES ====================
  /**
   * Validate ObjectId
   */
  isValidObjectId: (id) => {
    return /^[0-9a-fA-F]{24}$/.test(id);
  },

  /**
   * Format date
   */
  formatDate: (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  /**
   * Get status color
   */
  getStatusColor: (status) => {
    const colors = {
      active: 'green',
      draft: 'yellow',
      inactive: 'gray',
      archived: 'red'
    };
    return colors[status] || 'gray';
  },

  /**
   * Generate slug from name
   */
  generateSlug: (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }
};

export default adminCollectionService;